import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');
const fmtK = n => { const v=Number(n||0); return v>=1000000?'\u20a6'+(v/1000000).toFixed(1)+'M':v>=1000?'\u20a6'+(v/1000).toFixed(0)+'K':'\u20a6'+v.toLocaleString(); };

const DEPT_COLORS = { green:'#2E6B3A', amber:'#8B5E0A', red:'#8F2E24' };
const DEPT_BGS = { green:'#E8F5EB', amber:'#FFF3DC', red:'#FDEAEA' };
const DEPT_LABELS = { green:'Operating Normally', amber:'Attention Required', red:'Immediate Action Required' };

export default function EMD() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showThresholds, setShowThresholds] = useState(false);
  const [thresholds, setThresholds] = useState([]);

  const load = () => {
    setLoading(true);
    get('/emd').then(d => { setData(d); setLastRefresh(new Date()); setThresholds(d.thresholds||[]); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Auto refresh every 5 minutes
    const interval = setInterval(load, 300000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !data) return <div className="spinner" style={{marginTop:60}}></div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!data) return null;

  const { sales, job_orders, inventory, inventory_value, low_stock_count,
    pending_approvals, alerts, recent_reports, top_customers, monthly_trend,
    dept_health, order_status } = data;

  const jobByStatus = (status) => parseInt(job_orders.find(j=>j.status===status)?.count||0);

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title" style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem'}}>
            Executive Management Dashboard
          </h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Last updated: {lastRefresh.toLocaleTimeString('en-NG')} — Auto-refreshes every 5 minutes
          </p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      {/* Department Health Traffic Lights */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:20}}>
        {[
          ['Sales & CX', dept_health.sales],
          ['Manufacturing', dept_health.manufacturing],
          ['Inventory', dept_health.inventory],
          ['Finance', dept_health.finance],
        ].map(([dept, status]) => (
          <div key={dept} style={{background:DEPT_BGS[status],border:'1px solid '+DEPT_COLORS[status]+'44',
            borderRadius:10,padding:'12px 14px',display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:14,height:14,borderRadius:'50%',background:DEPT_COLORS[status],flexShrink:0,
              boxShadow:'0 0 6px '+DEPT_COLORS[status]}}></div>
            <div>
              <div style={{fontWeight:600,fontSize:12,color:DEPT_COLORS[status]}}>{dept}</div>
              <div style={{fontSize:10,color:DEPT_COLORS[status],opacity:.8}}>{DEPT_LABELS[status]}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Business Overview Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:10,marginBottom:20}}>
        {[
          { label:'Sales Today', value:fmtK(sales.sales_today), sub:sales.orders_today+' orders', color:'#556B2F' },
          { label:'Sales This Month', value:fmtK(sales.sales_this_month), sub:sales.orders_this_month+' orders', color:'#556B2F' },
          { label:'Active Productions', value:jobByStatus('in_progress')+jobByStatus('pending')+jobByStatus('approved'), sub:'Job orders in progress', color:'#1E4FAD' },
          { label:'Ready for Dispatch', value:jobByStatus('ready'), sub:'Completed, awaiting pickup', color:'#2E6B3A' },
          { label:'Outstanding Balance', value:fmtK(sales.outstanding_balance), sub:sales.outstanding_count+' invoices', color:'#8F2E24' },
          { label:'Inventory Value', value:fmtK(inventory_value), sub:low_stock_count+' items low stock', color:low_stock_count>0?'#8B5E0A':'#556B2F' },
        ].map(card => (
          <div key={card.label} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,
            padding:'14px 16px',boxShadow:'var(--shadow)'}}>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',
              color:'#8A8A8A',marginBottom:6}}>{card.label}</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,
              color:card.color,marginBottom:2}}>{card.value}</div>
            <div style={{fontSize:10,color:'#8A8A8A'}}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Pending Approvals Banner */}
      {(parseInt(pending_approvals.pending_refunds)+parseInt(pending_approvals.pending_pos)+
        parseInt(pending_approvals.pending_overtime)) > 0 && (
        <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,
          padding:'12px 20px',marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:13,fontWeight:500,color:'#8B5E0A'}}>
            ⚠ Pending Approvals:
            {parseInt(pending_approvals.pending_refunds)>0 && <span style={{marginLeft:12}}>🔴 {pending_approvals.pending_refunds} Refund{pending_approvals.pending_refunds!='1'?'s':''}</span>}
            {parseInt(pending_approvals.pending_pos)>0 && <span style={{marginLeft:12}}>📦 {pending_approvals.pending_pos} Purchase Order{pending_approvals.pending_pos!='1'?'s':''}</span>}
            {parseInt(pending_approvals.pending_overtime)>0 && <span style={{marginLeft:12}}>⏰ {pending_approvals.pending_overtime} Overtime Request{pending_approvals.pending_overtime!='1'?'s':''}</span>}
          </div>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
        {/* Alerts */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
          <div style={{background:'#3B2A21',padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
              🔔 Alerts & Exceptions
            </span>
            <span style={{fontSize:10,color:'rgba(242,239,231,.5)'}}>{alerts.length} active</span>
          </div>
          <div style={{maxHeight:280,overflowY:'auto'}}>
            {alerts.length === 0 ? (
              <div style={{padding:20,textAlign:'center',color:'#8A8A8A',fontSize:12}}>No alerts — all systems normal ✓</div>
            ) : alerts.map((a,i) => (
              <div key={i} style={{padding:'10px 16px',borderBottom:'1px solid #F2EFE7',
                background:a.severity==='danger'?'#FFF8F8':'#FFFBF0'}}>
                <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                  <span style={{fontSize:14,flexShrink:0}}>{a.alert_type==='low_stock'?'📦':a.alert_type==='overdue_job'?'🏭':a.alert_type==='pending_refund'?'💰':'⚠'}</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:a.severity==='danger'?'#8F2E24':'#8B5E0A'}}>{a.title}</div>
                    <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{a.message}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue Trend */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
          <div style={{background:'#556B2F',padding:'12px 16px'}}>
            <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
              📈 Revenue Trend (6 Months)
            </span>
          </div>
          <div style={{padding:16}}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthly_trend}>
                <XAxis dataKey="month" tick={{fontSize:10}} />
                <YAxis tick={{fontSize:10}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                <Tooltip formatter={v=>fmt(v)} />
                <Bar dataKey="revenue" fill="#556B2F" radius={[4,4,0,0]} name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
        {/* Job Orders Status */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
          <div style={{background:'#1E4FAD',padding:'12px 16px'}}>
            <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#F2EFE7',fontWeight:600}}>
              🏭 Production Status
            </span>
          </div>
          <div style={{padding:16}}>
            {[
              ['Pending', 'pending', '#8B5E0A', '#FFF3DC'],
              ['Approved', 'approved', '#1E4FAD', '#EDF4FF'],
              ['In Progress', 'in_progress', '#556B2F', '#E8F5EB'],
              ['Ready', 'ready', '#2E6B3A', '#D4EDDA'],
              ['Dispatched', 'dispatched', '#6C757D', '#F8F9FA'],
              ['Completed', 'completed', '#155724', '#D4EDDA'],
            ].map(([label, status, color, bg]) => {
              const count = jobByStatus(status);
              return (
                <div key={status} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                  padding:'8px 12px',background:bg,borderRadius:6,marginBottom:6}}>
                  <span style={{fontSize:12,fontWeight:500,color}}>{label}</span>
                  <span style={{fontSize:18,fontWeight:700,fontFamily:'Cormorant Garamond,serif',color}}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Customers */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
          <div style={{background:'#3B2A21',padding:'12px 16px'}}>
            <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
              👥 Top Customers This Month
            </span>
          </div>
          <div style={{padding:16}}>
            {top_customers.length === 0 ? (
              <div style={{textAlign:'center',color:'#8A8A8A',fontSize:12,padding:20}}>No orders this month yet</div>
            ) : top_customers.map((c,i) => (
              <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                padding:'8px 0',borderBottom:'1px solid #F2EFE7'}}>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:'#3B2A21'}}>{c.name}</div>
                  <div style={{fontSize:10,color:'#8A8A8A'}}>{c.orders} order{c.orders!='1'?'s':''}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600,color:'#556B2F'}}>{fmt(c.total_billed)}</div>
                  <div style={{fontSize:10,color:'var(--paid)'}}>Paid: {fmt(c.total_paid)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Low Stock Alert */}
      {low_stock_count > 0 && (
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',marginBottom:20}}>
          <div style={{background:'#8B5E0A',padding:'12px 16px'}}>
            <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#F2EFE7',fontWeight:600}}>
              📦 Low Stock Alerts ({low_stock_count} items)
            </span>
          </div>
          <div style={{padding:0}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'#F2EFE7'}}>
                {['Material','Current Stock','Reorder Level','Unit Cost','Stock Value'].map(h=>(
                  <th key={h} style={{padding:'8px 14px',textAlign:'left',fontSize:9,color:'#556B2F',
                    fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {inventory.filter(m=>parseFloat(m.current_stock)<=parseFloat(m.reorder_level)).map((m,i)=>(
                  <tr key={i} style={{background:i%2===0?'#fff':'#FFF8F0'}}>
                    <td style={{padding:'8px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:500,color:'#8F2E24'}}>{m.name}</td>
                    <td style={{padding:'8px 14px',borderBottom:'1px solid #EDE7D8',color:'#8F2E24',fontWeight:600}}>
                      {Number(m.current_stock).toLocaleString()} {m.unit_of_measure}
                    </td>
                    <td style={{padding:'8px 14px',borderBottom:'1px solid #EDE7D8'}}>{Number(m.reorder_level).toLocaleString()}</td>
                    <td style={{padding:'8px 14px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif'}}>{fmt(m.weighted_avg_cost)}</td>
                    <td style={{padding:'8px 14px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',color:'#556B2F'}}>{fmt(m.stock_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Team Reports */}
      <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
        <div style={{background:'#556B2F',padding:'12px 16px'}}>
          <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
            📋 Recent Team Reports
          </span>
        </div>
        <div style={{padding:16}}>
          {recent_reports.length === 0 ? (
            <div style={{textAlign:'center',color:'#8A8A8A',fontSize:12}}>No reports submitted yet</div>
          ) : recent_reports.map((r,i) => (
            <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
              padding:'8px 0',borderBottom:'1px solid #F2EFE7'}}>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:'#3B2A21',textTransform:'capitalize'}}>
                  {r.report_type.replace(/_/g,' ')} — {r.frequency}
                </div>
                <div style={{fontSize:11,color:'#8A8A8A'}}>{r.period_label} | {r.submitted_by_name}</div>
              </div>
              <div style={{fontSize:11,color:'#8A8A8A'}}>{new Date(r.created_at).toLocaleDateString('en-NG')}</div>
            </div>
          ))}
          <a href="/team-reports" style={{display:'block',marginTop:10,fontSize:12,color:'#556B2F',
            fontWeight:600,textDecoration:'none',textAlign:'center'}}>View All Reports →</a>
        </div>
      </div>
      {showThresholds && (
        <ThresholdModal thresholds={thresholds} onClose={()=>setShowThresholds(false)}
          onSaved={()=>{setShowThresholds(false);load();}} />
      )}
    </div>
  );
}

function ThresholdModal({ thresholds, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    const f = {};
    thresholds.forEach(t => { f[t.department] = { ...t }; });
    setForm(f);
  }, [thresholds]);

  const DEPT_LABELS = {
    sales: { label:'Sales & CX', m1:'Pending Refunds (Red)', m2:'Outstanding Invoices Count (Red)', m1a:'Pending Refunds (Amber)', m2a:'Outstanding Invoices Count (Amber)' },
    manufacturing: { label:'Manufacturing', m1:'Overdue Job Orders (Red)', m2:'Overdue Job Orders (Red)', m1a:'Overdue Job Orders (Amber)', m2a:'Overdue Job Orders (Amber)' },
    inventory: { label:'Inventory', m1:'Low Stock Items (Red)', m2:'Low Stock Items (Red)', m1a:'Low Stock Items (Amber)', m2a:'Low Stock Items (Amber)' },
    finance: { label:'Finance', m1:'Outstanding Balance ₦ (Red)', m2:'Outstanding Balance ₦ (Red)', m1a:'Outstanding Balance ₦ (Amber)', m2a:'Outstanding Balance ₦ (Amber)' },
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const dept of Object.keys(form)) {
        const { put } = await import('../utils/api');
        await put('/emd/thresholds/'+dept, form[dept]);
      }
      onSaved();
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:600}}>
        <div className="modal-hdr">
          <span className="modal-title">⚙ Configure EMD Thresholds</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          <p style={{fontSize:12,color:'#8A8A8A',marginBottom:16}}>
            Set the values at which each department turns amber (warning) or red (critical).
          </p>
          {Object.entries(DEPT_LABELS).map(([dept, labels]) => (
            <div key={dept} style={{marginBottom:20,background:'#F2EFE7',borderRadius:8,padding:16}}>
              <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:12}}>{labels.label}</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group" style={{margin:0}}>
                  <label className="form-label" style={{fontSize:10}}>🟡 Amber threshold</label>
                  <input type="number" className="form-control" style={{fontSize:12}}
                    value={form[dept]?.metric_1_amber||''}
                    onChange={e=>setForm({...form,[dept]:{...form[dept],metric_1_amber:e.target.value,metric_2_amber:e.target.value}})} />
                  <div className="form-hint" style={{fontSize:10}}>{labels.m1a}</div>
                </div>
                <div className="form-group" style={{margin:0}}>
                  <label className="form-label" style={{fontSize:10}}>🔴 Red threshold</label>
                  <input type="number" className="form-control" style={{fontSize:12}}
                    value={form[dept]?.metric_1_red||''}
                    onChange={e=>setForm({...form,[dept]:{...form[dept],metric_1_red:e.target.value,metric_2_red:e.target.value}})} />
                  <div className="form-hint" style={{fontSize:10}}>{labels.m1}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving?'Saving...':'Save Thresholds'}
          </button>
        </div>
      </div>
    </div>
  );
}
